import React, { useMemo, useRef, useState } from 'react';
import { Check, ChevronsUpDown, Search } from 'lucide-react';
import { cn } from 'src/lib/utils';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';

let nextFilterComboboxId = 0;

function normalize(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

export function FilterCombobox({
  options = [],
  value,
  onValueChange,
  label,
  searchPlaceholder,
  emptyMessage,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const searchRef = useRef(null);
  const idRef = useRef(null);

  if (idRef.current === null) {
    nextFilterComboboxId += 1;
    idRef.current = `kanban-filter-options-${nextFilterComboboxId}`;
  }

  const listId = idRef.current;
  const visibleOptions = useMemo(() => {
    const normalizedQuery = normalize(query);
    if (!normalizedQuery) return options;

    return options.filter((option) =>
      normalize(`${option.label} ${option.searchText || ''}`).includes(
        normalizedQuery,
      ),
    );
  }, [options, query]);
  const selectedOption = options.find(
    (option) => String(option.value) === String(value),
  );
  const selectedLabel = selectedOption ? selectedOption.label : label;
  const safeActiveIndex =
    visibleOptions.length === 0
      ? -1
      : Math.min(activeIndex, visibleOptions.length - 1);

  const close = () => {
    setOpen(false);
    setQuery('');
  };

  const selectOption = (option) => {
    onValueChange(String(option.value));
    close();
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === 'ArrowDown' && visibleOptions.length > 0) {
      event.preventDefault();
      setActiveIndex((index) => (index + 1) % visibleOptions.length);
    } else if (event.key === 'ArrowUp' && visibleOptions.length > 0) {
      event.preventDefault();
      setActiveIndex(
        (index) => (index - 1 + visibleOptions.length) % visibleOptions.length,
      );
    } else if (event.key === 'Home' && visibleOptions.length > 0) {
      event.preventDefault();
      setActiveIndex(0);
    } else if (event.key === 'End' && visibleOptions.length > 0) {
      event.preventDefault();
      setActiveIndex(visibleOptions.length - 1);
    } else if (event.key === 'Enter' && safeActiveIndex >= 0) {
      event.preventDefault();
      selectOption(visibleOptions[safeActiveIndex]);
    }
  };

  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (nextOpen) {
          const selectedIndex = visibleOptions.findIndex(
            (option) => String(option.value) === String(value),
          );
          setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
        } else {
          setQuery('');
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-label={label}
          aria-expanded={open}
          aria-controls={listId}
          aria-haspopup="listbox"
          className="h-9 w-full justify-between bg-white px-3 font-normal"
        >
          <span className="truncate">{selectedLabel}</span>
          <ChevronsUpDown
            className="ml-2 h-4 w-4 shrink-0 opacity-50"
            aria-hidden="true"
          />
        </Button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          searchRef.current?.focus();
        }}
        className="w-[var(--radix-popover-trigger-width)] p-0"
      >
        <div className="border-b p-2">
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground"
              aria-hidden="true"
            />
            <Input
              ref={searchRef}
              aria-label={searchPlaceholder}
              aria-autocomplete="list"
              aria-controls={listId}
              aria-activedescendant={
                safeActiveIndex >= 0
                  ? `${listId}-option-${safeActiveIndex}`
                  : undefined
              }
              placeholder={searchPlaceholder}
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setActiveIndex(0);
              }}
              onKeyDown={handleSearchKeyDown}
              className="h-9 bg-transparent pl-8"
            />
          </div>
        </div>

        <div
          id={listId}
          role="listbox"
          aria-label={label}
          className="max-h-96 overflow-y-auto p-1"
        >
          {visibleOptions.length === 0 ? (
            <div
              role="status"
              className="px-2 py-4 text-sm text-muted-foreground"
            >
              {emptyMessage}
            </div>
          ) : (
            visibleOptions.map((option, index) => {
              const isSelected = String(option.value) === String(value);
              const isActive = index === safeActiveIndex;

              return (
                <div
                  id={`${listId}-option-${index}`}
                  key={String(option.value)}
                  role="option"
                  aria-selected={isSelected}
                  data-active={isActive}
                  onMouseMove={() => setActiveIndex(index)}
                  onClick={() => selectOption(option)}
                  className={cn(
                    'flex cursor-pointer items-center rounded-sm px-2 py-2 text-sm outline-none transition-colors hover:bg-accent/30 focus-visible:ring-1 focus-visible:ring-ring data-[active=true]:bg-accent/30',
                    isSelected &&
                      'bg-accent/60 font-medium text-accent-foreground',
                  )}
                >
                  <span className="flex-1 truncate">{option.label}</span>
                  <Check
                    className={cn(
                      'ml-2 h-4 w-4',
                      isSelected ? 'opacity-100' : 'opacity-0',
                    )}
                    aria-hidden="true"
                  />
                </div>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default FilterCombobox;
